import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../../firebase/config";
import {
  LOCATION_AREA_ERROR,
  buildBroadLocationFromGoogleResult,
  normalizeBroadLocation,
  validateBroadLocationInput,
} from "../../utils/locationArea";
import {
  LOCATION_SOURCES,
  buildCanonicalLocationPayload,
  hasValidCoordinates,
  normalizeLocationRecord,
  selectRecordLocation,
} from "../../utils/locationUtils";
import {
  EMAIL_VERIFICATION_REQUIRED_MESSAGE,
  canPerformTrustedAction,
  requireVerifiedEmail,
} from "../../utils/emailVerification";
import FarmAvatar from "../Common/FarmAvatar";
import { getProfileRoleLabel } from "../../utils/profileRoles";
import { useNavigate } from "react-router-dom";
import {
  LAND_AVAILABILITY_OPTIONS,
  getLandAvailabilityLabel,
} from "../../constants/availability";
import BarterExchangeSection from "../Common/BarterExchangeSection";
import {
  abandonAppFlow,
  completeAppFlow,
  startAppFlow,
  trackAppEvent,
} from "../../utils/appAnalytics";
import {
  DEFAULT_BARTER_FIELDS,
  WANT_AD_URGENCY_OPTIONS,
  WANTED_CATEGORY_OPTIONS,
  buildBarterPayload,
  getExchangeBadges,
  getWantedCategoryLabel,
} from "../../utils/barter";
import {
  COMMUNITY_ALERT_DURATION_OPTIONS,
  COMMUNITY_ALERT_TYPE,
  getAlertExpiresAtForDuration,
  getCommunityAlertExpiryLabel,
} from "../../utils/communityAlerts";

export const COMMUNITY_TYPES = [
  { value: "general", label: "General" },
  { value: "question", label: "Question" },
  { value: "looking_for", label: "Looking For" },
  { value: "want_ad", label: "Looking For / Want-Ad" },
  { value: "land_use", label: "Land Use" },
  { value: "advice", label: "Advice Needed" },
  { value: "farm_photo", label: "Farm Photo" },
  { value: "local_alert", label: "Local Alert" },
  { value: "recommendation", label: "Recommendation" },
];

export const LAND_USE_TYPES = [
  { value: "horse_grazing", label: "Horse grazing pasture" },
  { value: "livestock_grazing", label: "Livestock grazing" },
  { value: "fenced_dog_run", label: "Fenced dog run" },
  { value: "garden_plot", label: "Garden plot" },
  { value: "hay_field_access", label: "Hay field access" },
  { value: "equipment_parking", label: "Equipment parking" },
  { value: "temporary_holding", label: "Temporary holding area" },
  { value: "other", label: "Other land use" },
];

const getLandUseTypeLabel = (value) =>
  LAND_USE_TYPES.find((item) => item.value === value)?.label || "Land use";

const normalizeUnknownBoolean = (value) => {
  if (value === "true") return true;
  if (value === "false") return false;
  return "unknown";
};

const initialLandUseDetails = {
  landUseType: "horse_grazing",
  offeringOrSeeking: "offering",
  acreage: "",
  fenced: "unknown",
  waterAccess: "unknown",
  animalAllowedNotes: "",
  priceType: "contact",
  price: "",
  landAvailabilityStatus: "available_now",
  availableStarting: "",
  availabilityNote: "",
};

const getLocationSeed = (userLocation, userProfile) => {
  return (
    selectRecordLocation({
      savedProfile: userProfile,
      browserLocation: userLocation,
      allowBrowserLocation: true,
    }) || normalizeLocationRecord({})
  );
};

const geocodeLocation = async (locationText) => {
  if (!locationText.trim()) return null;
  if (!window.google?.maps?.Geocoder) return null;

  const geocoder = new window.google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode({ address: locationText.trim() }, (results, status) => {
      if (status !== "OK" || !results?.[0]) {
        resolve(null);
        return;
      }

      const result = results[0];
      const broadLocation = buildBroadLocationFromGoogleResult(result, locationText);

      resolve(broadLocation);
    });
  });
};

const MAX_PHOTO_SIZE = 5 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const GENERIC_POST_FAILURE_MESSAGE =
  "We could not create your post. Please check your connection and try again.";

const getTypeLabel = (type) =>
  COMMUNITY_TYPES.find((item) => item.value === type)?.label || "General";

const CreateCommunityPost = ({
  currentUser,
  userProfile,
  userLocation,
  initialType = "general",
  onCreated,
}) => {
  const navigate = useNavigate();
  const locationSeed = useMemo(
    () => getLocationSeed(userLocation, userProfile),
    [userLocation, userProfile]
  );

  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [type, setType] = useState(initialType || "general");
  const [wantedCategory, setWantedCategory] = useState("farm_goods");
  const [budgetText, setBudgetText] = useState("");
  const [urgency, setUrgency] = useState("just_browsing");
  const [alertDuration, setAlertDuration] = useState("24h");
  const [barterFields, setBarterFields] = useState(DEFAULT_BARTER_FIELDS);
  const [landUseDetails, setLandUseDetails] = useState(initialLandUseDetails);
  const [locationText, setLocationText] = useState(locationSeed.locationText);
  const [pinned, setPinned] = useState(false);
  const [isFounderPost, setIsFounderPost] = useState(false);
  const [notify, setNotify] = useState(false);
  const [showAdminOptions, setShowAdminOptions] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState("");
  const [imagePositionX, setImagePositionX] = useState(50);
  const [imagePositionY, setImagePositionY] = useState(50);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const isAdmin = userProfile?.isAdmin === true || userProfile?.admin === true;
  const communityFlowStartedRef = useRef(false);

  useEffect(() => {
    if (!photo) {
      setPhotoPreviewUrl("");
      return undefined;
    }

    const url = URL.createObjectURL(photo);
    setPhotoPreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const resetForm = () => {
    setTitle("");
    setText("");
    setType(initialType || "general");
    setWantedCategory("farm_goods");
    setBudgetText("");
    setUrgency("just_browsing");
    setAlertDuration("24h");
    setBarterFields(DEFAULT_BARTER_FIELDS);
    setLandUseDetails(initialLandUseDetails);
    setPinned(false);
    setIsFounderPost(false);
    setNotify(false);
    setShowAdminOptions(false);
    setPhoto(null);
    setImagePositionX(50);
    setImagePositionY(50);
    setIsPreviewing(false);
    setFieldErrors({});
  };

  const clearFeedback = () => {
    setFormError("");
    setFormSuccess("");
  };

  const reportPostCreateFailure = (stage, error, overrides = {}) => {
    const errorCode = String(error?.code || overrides.errorCode || stage || "unknown");
    const errorSummary = String(
      error?.message || overrides.errorSummary || GENERIC_POST_FAILURE_MESSAGE
    );

    trackAppEvent("community_post_create_failed", {
      feature: "community",
      action: "create_post",
      status: "failed",
      step: stage,
      errorCode,
      errorSummary,
      ...overrides,
    });
  };

  useEffect(() => {
    if (!title && !text) {
      setType(initialType || "general");
    }
  }, [initialType, title, text]);

  useEffect(() => {
    if (!currentUser?.uid || communityFlowStartedRef.current) return;
    if (!title.trim() && !text.trim() && !photo) return;

    communityFlowStartedRef.current = true;
    startAppFlow("community_post", "community_post_started", {
      feature: "community",
      action: "create_post",
      abandonEventType: "community_post_abandoned",
    });
  }, [currentUser?.uid, photo, text, title]);

  useEffect(() => {
    return () => {
      if (communityFlowStartedRef.current) {
        abandonAppFlow("community_post");
      }
    };
  }, []);

  const clampImagePosition = (value) => Math.min(100, Math.max(0, value));

  const nudgeImagePosition = (axis, amount) => {
    if (axis === "x") {
      setImagePositionX((value) => clampImagePosition(value + amount));
      return;
    }

    setImagePositionY((value) => clampImagePosition(value + amount));
  };

  const resetImagePosition = () => {
    setImagePositionX(50);
    setImagePositionY(50);
  };

  const validateDraft = () => {
    const cleanTitle = title.trim();
    const cleanText = text.trim();
    const nextFieldErrors = {};

    if (!cleanTitle) {
      nextFieldErrors.title = type === "want_ad"
        ? "Add what you are looking for."
        : "Add a title.";
    }

    if (!cleanText) {
      nextFieldErrors.text = type === "want_ad"
        ? "Add a description."
        : "Add a message.";
    }

    if (type === "land_use" && !landUseDetails.landUseType) {
      nextFieldErrors.landUseType = "Choose a land use type.";
    }

    const cleanLocation = locationText.trim();
    if (cleanLocation) {
      const validation = validateBroadLocationInput(cleanLocation);
      if (!validation.ok) {
        nextFieldErrors.locationText = validation.error || LOCATION_AREA_ERROR;
      }
    }

    setFieldErrors(nextFieldErrors);

    if (Object.keys(nextFieldErrors).length > 0) {
      const message =
        nextFieldErrors.locationText ||
        nextFieldErrors.title ||
        nextFieldErrors.text ||
        nextFieldErrors.landUseType ||
        "Please fix the highlighted fields and try again.";
      setFormError(message);
      reportPostCreateFailure("validation", null, {
        errorCode: "validation",
        errorSummary: message,
      });
      return false;
    }

    setFormError("");
    return true;
  };

  const handleLandUseChange = (event) => {
    const { name, value } = event.target;
    clearFeedback();
    setFieldErrors((current) => ({ ...current, [name]: "" }));
    setLandUseDetails((current) => ({ ...current, [name]: value }));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    clearFeedback();

    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      const message = "Please choose a JPEG, PNG, or WebP image.";
      setFieldErrors((current) => ({ ...current, photo: message }));
      setFormError(message);
      reportPostCreateFailure("photo_validation", null, {
        errorCode: "invalid_photo_type",
        errorSummary: message,
      });
      event.target.value = "";
      return;
    }

    if (file.size > MAX_PHOTO_SIZE) {
      const message = "Please choose an image under 5MB.";
      setFieldErrors((current) => ({ ...current, photo: message }));
      setFormError(message);
      reportPostCreateFailure("photo_validation", null, {
        errorCode: "photo_too_large",
        errorSummary: message,
      });
      event.target.value = "";
      return;
    }

    setFieldErrors((current) => ({ ...current, photo: "" }));
    setPhoto(file);
  };

  const handlePreview = (event) => {
    event.preventDefault();
    clearFeedback();
    if (!validateDraft()) return;
    setIsPreviewing(true);
  };

  const publishPost = async () => {
    if (submitting) return;
    clearFeedback();

    if (!currentUser?.uid) {
      const message = "Please log in to post.";
      setFormError(message);
      reportPostCreateFailure("auth", null, {
        errorCode: "not_signed_in",
        errorSummary: message,
      });
      return;
    }

    if (!canPerformTrustedAction(currentUser, userProfile)) {
      setFormError(EMAIL_VERIFICATION_REQUIRED_MESSAGE);
      reportPostCreateFailure("verification", null, {
        errorCode: "email_not_verified",
        errorSummary: EMAIL_VERIFICATION_REQUIRED_MESSAGE,
      });
      return;
    }

    if (!requireVerifiedEmail(currentUser, userProfile)) return;
    if (!validateDraft()) return;

    const cleanTitle = title.trim();
    const cleanText = text.trim();

    try {
      setSubmitting(true);

      let lat = hasValidCoordinates(locationSeed) ? locationSeed.lat : null;
      let lng = hasValidCoordinates(locationSeed) ? locationSeed.lng : null;
      let finalLocationText = locationText.trim();
      const typedLocationChanged =
        finalLocationText &&
        finalLocationText !== String(locationSeed.locationText || "").trim();

      if (typedLocationChanged) {
        lat = null;
        lng = null;
      }

      let locationDetails = normalizeBroadLocation(
        {
          city: locationSeed.city,
          state: locationSeed.state,
          county: locationSeed.county,
          locationText: finalLocationText,
        },
        finalLocationText
      );

      if (finalLocationText) {
        const validation = validateBroadLocationInput(finalLocationText);
        if (!validation.ok) {
          const message = validation.error || LOCATION_AREA_ERROR;
          setFieldErrors((current) => ({ ...current, locationText: message }));
          setFormError(message);
          reportPostCreateFailure("location_validation", null, {
            errorCode: "invalid_location",
            errorSummary: message,
          });
          setSubmitting(false);
          return;
        }
      }

      if (finalLocationText && (typedLocationChanged || lat === null || lng === null)) {
        const geocoded = await geocodeLocation(finalLocationText);
        if (geocoded) {
          lat = geocoded.lat;
          lng = geocoded.lng;
          locationDetails = normalizeBroadLocation(geocoded, finalLocationText);
          finalLocationText = locationDetails.locationText;
        }
      }

      const shouldNotifyCommunity = isAdmin ? notify : false;
      const shouldDisplayAsBrand = isAdmin && isFounderPost;
      const postRef = doc(collection(db, "communityPosts"));
      const imagePath = photo ? `communityPosts/${postRef.id}/post-image` : "";
      const locationPayload = buildCanonicalLocationPayload({
        ...locationDetails,
        lat,
        lng,
        locationText: finalLocationText,
        locationSource: typedLocationChanged
          ? LOCATION_SOURCES.EXPLICIT
          : LOCATION_SOURCES.SAVED_PROFILE,
      });
      const landUsePayload =
        type === "land_use"
          ? {
              ownerId: currentUser.uid,
              description: cleanText,
              landUseType: landUseDetails.landUseType,
              offeringOrSeeking: landUseDetails.offeringOrSeeking,
              acreage: landUseDetails.acreage.trim(),
              fenced: normalizeUnknownBoolean(landUseDetails.fenced),
              waterAccess: normalizeUnknownBoolean(landUseDetails.waterAccess),
              animalAllowedNotes: landUseDetails.animalAllowedNotes.trim(),
              priceType: landUseDetails.priceType,
              price: landUseDetails.price.trim(),
              landAvailabilityStatus: landUseDetails.landAvailabilityStatus,
              availableStarting:
                landUseDetails.landAvailabilityStatus === "available_starting"
                  ? landUseDetails.availableStarting
                  : "",
              availabilityNote:
                landUseDetails.landAvailabilityStatus === "available_starting"
                  ? landUseDetails.availabilityNote.trim()
                  : "",
            }
          : {};
      const wantAdPayload =
        type === "want_ad"
          ? {
              postType: "want_ad",
              wantedCategory,
              wantedTitle: cleanTitle,
              wantedDescription: cleanText,
              budgetText: budgetText.trim(),
              urgency,
              ...buildBarterPayload(barterFields),
            }
          : {
              postType: "standard",
            };
      const alertExpiresAt =
        type === COMMUNITY_ALERT_TYPE
          ? getAlertExpiresAtForDuration(alertDuration)
          : null;
      const alertPayload =
        type === COMMUNITY_ALERT_TYPE
          ? {
              alertStatus: "active",
              alertSeverity: "urgent",
              alertExpiresAt,
              alertResolvedAt: null,
            }
          : {};

      try {
        await setDoc(postRef, {
        authorId: currentUser.uid,
        createdByAdminId: shouldDisplayAsBrand ? currentUser.uid : null,
        authorName:
          shouldDisplayAsBrand
            ? "MyFarmingPal"
            : userProfile?.name || currentUser.displayName || currentUser.email || "Community member",
        authorPhoto: shouldDisplayAsBrand
          ? ""
          : userProfile?.profilePicture || currentUser.photoURL || "",
        authorFarmAnimal: shouldDisplayAsBrand ? "" : userProfile?.farmAnimal || "",
        authorFarmAnimalSelectionExplicit:
          !shouldDisplayAsBrand &&
          userProfile?.farmAnimalSelectionExplicit === true,
        authorRoles: Array.isArray(userProfile?.roles)
          ? userProfile.roles
          : userProfile?.type
          ? [userProfile.type]
          : [],
        authorRating: Number(userProfile?.rating || 0),
        authorReviewCount: Number(userProfile?.reviewCount || 0),

        type,
        title: cleanTitle,
        text: cleanText,
        ...landUsePayload,
        ...wantAdPayload,
        ...alertPayload,
        imageUrl: "",
        imagePath,
        hasImage: false,
        imagePositionX,
        imagePositionY,

        ...locationPayload,
        locationNote: finalLocationText,

        visibilityRadiusMiles: 100,
        likeCount: 0,
        commentCount: 0,
        helpfulCount: 0,

        isArchived: false,
        isHidden: false,
        status: "active",
        pinned: isAdmin ? pinned : false,
        isFounderPost: shouldDisplayAsBrand,
        displayAsBrand: shouldDisplayAsBrand,
        notify: shouldNotifyCommunity,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        });
      } catch (error) {
        reportPostCreateFailure("firestore_create", error, {
          relatedId: postRef.id,
        });
        error.communityFailureTracked = true;
        throw error;
      }

      if (photo) {
        try {
          const imageRef = ref(storage, imagePath);
          await uploadBytes(imageRef, photo, { contentType: photo.type });
          const imageUrl = await getDownloadURL(imageRef);

          await updateDoc(postRef, {
            imageUrl,
            imagePath,
            hasImage: true,
            updatedAt: serverTimestamp(),
          });
        } catch (error) {
          reportPostCreateFailure("image_upload", error, {
            relatedId: postRef.id,
          });
          error.communityFailureTracked = true;
          throw error;
        }
      }

      completeAppFlow("community_post", "community_post_completed", {
        feature: "community",
        action: "create_post",
        relatedId: postRef.id,
      });
      communityFlowStartedRef.current = false;
      resetForm();
      setFormSuccess("Posted. Your post is live in the Community feed.");
      onCreated?.();
    } catch (error) {
      console.error("Failed creating community post:", error);
      const message =
        error?.code === "permission-denied"
          ? "We could not post this because your account is not allowed to write community posts yet. Please verify your email and try again."
          : GENERIC_POST_FAILURE_MESSAGE;
      if (!error?.communityFailureTracked) {
        reportPostCreateFailure("unknown", error, {
          errorSummary: message,
        });
      }
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const previewDisplaysAsBrand = isAdmin && isFounderPost;
  const authorName = previewDisplaysAsBrand
    ? "MyFarmingPal"
    : userProfile?.name || currentUser?.displayName || currentUser?.email || "Community member";
  const authorPhoto = previewDisplaysAsBrand
    ? ""
    : userProfile?.profilePicture || currentUser?.photoURL || "";
  const previewLocationLabel = locationText.trim() || locationSeed.locationText || "Location not listed";
  const isLandUsePost = type === "land_use";
  const isWantAdPost = type === "want_ad";
  const isLocalAlertPost = type === COMMUNITY_ALERT_TYPE;
  const previewAlertPost = isLocalAlertPost
    ? {
        type,
        alertStatus: "active",
        alertExpiresAt: getAlertExpiresAtForDuration(alertDuration),
      }
    : null;

  return (
    <form className="community-create-card" onSubmit={handlePreview}>
      <div className="community-card-heading">
        <div>
          <p className="eyebrow">Local board</p>
          <h3>Share with your farm community</h3>
        </div>
        <span>100 mile default</span>
      </div>

      <div className="context-tip">
        <strong>Tip:</strong> A short local question, introduction, or helpful
        update is perfect. Keep private contact details out of public posts.
      </div>

      <div className="community-form-grid">
        <label className="form-label">
          {isWantAdPost ? "What are you looking for?" : "Title"}
          <input
            type="text"
            value={title}
            onChange={(event) => {
              clearFeedback();
              setFieldErrors((current) => ({ ...current, title: "" }));
              setTitle(event.target.value);
            }}
            maxLength={120}
            placeholder={isWantAdPost ? "Looking for tomato starts near Normandy..." : "Need hay help near Franklin..."}
            aria-invalid={Boolean(fieldErrors.title)}
            aria-describedby={fieldErrors.title ? "community-title-error" : undefined}
          />
          {fieldErrors.title && (
            <span id="community-title-error" className="community-inline-error">
              {fieldErrors.title}
            </span>
          )}
        </label>

        <label className="form-label">
          Category
          <select value={type} onChange={(event) => setType(event.target.value)}>
            {COMMUNITY_TYPES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="form-label">
        {isWantAdPost ? "Description" : "Message"}
        <textarea
          value={text}
          onChange={(event) => {
            clearFeedback();
            setFieldErrors((current) => ({ ...current, text: "" }));
            setText(event.target.value);
          }}
          rows={5}
          maxLength={1800}
          placeholder={isWantAdPost ? "Tell nearby folks what you need, condition, timing, and what you can pay or trade..." : "Ask a question, share a local update, or recommend someone reliable..."}
          aria-invalid={Boolean(fieldErrors.text)}
          aria-describedby={fieldErrors.text ? "community-text-error" : undefined}
        />
        {fieldErrors.text && (
          <span id="community-text-error" className="community-inline-error">
            {fieldErrors.text}
          </span>
        )}
      </label>

      {isWantAdPost && (
        <section className="want-ad-form-panel">
          <div className="community-card-heading">
            <div>
              <p className="eyebrow">Want-Ad</p>
              <h3>Help nearby folks understand what you need</h3>
            </div>
          </div>

          <div className="community-form-grid">
            <label className="form-label">
              Wanted category
              <select
                value={wantedCategory}
                onChange={(event) => setWantedCategory(event.target.value)}
              >
                {WANTED_CATEGORY_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="form-label">
              Budget or trade note optional
              <input
                value={budgetText}
                onChange={(event) => setBudgetText(event.target.value)}
                maxLength={120}
                placeholder="Around $50, trade preferred, depends..."
              />
            </label>

            <label className="form-label">
              Timing
              <select value={urgency} onChange={(event) => setUrgency(event.target.value)}>
                {WANT_AD_URGENCY_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <BarterExchangeSection
            value={barterFields}
            onChange={setBarterFields}
            title="Cash / trade options"
          />
        </section>
      )}

      {isLandUsePost && (
        <section className="land-use-form-panel">
          <div className="community-card-heading">
            <div>
              <p className="eyebrow">Land Share</p>
              <h3>Offer or find local land access</h3>
            </div>
          </div>
          <p className="input-helper">
            Offer or find pasture, grazing space, fenced areas, garden plots, or field access.
          </p>

          <div className="land-use-map-callout">
            <div>
              <strong>Show your land on the map</strong>
              <span>
                Draw the pasture, garden plot, fenced area, or field access area so nearby people
                can see what is available.
              </span>
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate("/map", { state: { postType: "land_use" } })}
            >
              Draw land area on map
            </button>
          </div>

          <div className="community-form-grid">
            <label className="form-label">
              Land use type
              <select
                name="landUseType"
                value={landUseDetails.landUseType}
                onChange={handleLandUseChange}
                aria-invalid={Boolean(fieldErrors.landUseType)}
                aria-describedby={fieldErrors.landUseType ? "community-land-use-error" : undefined}
              >
                {LAND_USE_TYPES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {fieldErrors.landUseType && (
                <span id="community-land-use-error" className="community-inline-error">
                  {fieldErrors.landUseType}
                </span>
              )}
            </label>

            <label className="form-label">
              Offering or seeking
              <select
                name="offeringOrSeeking"
                value={landUseDetails.offeringOrSeeking}
                onChange={handleLandUseChange}
              >
                <option value="offering">Offering</option>
                <option value="seeking">Seeking</option>
              </select>
            </label>

            <label className="form-label">
              Acreage optional
              <input
                name="acreage"
                value={landUseDetails.acreage}
                onChange={handleLandUseChange}
                placeholder="2 acres, half acre, small lot..."
              />
            </label>

            <label className="form-label">
              Price type
              <select
                name="priceType"
                value={landUseDetails.priceType}
                onChange={handleLandUseChange}
              >
                <option value="free">Free</option>
                <option value="paid">Paid</option>
                <option value="trade">Trade</option>
                <option value="contact">Contact</option>
              </select>
            </label>

            <label className="form-label">
              Fenced
              <select name="fenced" value={landUseDetails.fenced} onChange={handleLandUseChange}>
                <option value="unknown">Unknown</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </label>

            <label className="form-label">
              Water access
              <select
                name="waterAccess"
                value={landUseDetails.waterAccess}
                onChange={handleLandUseChange}
              >
                <option value="unknown">Unknown</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </label>

            <label className="form-label">
              Price optional
              <input
                name="price"
                value={landUseDetails.price}
                onChange={handleLandUseChange}
                placeholder="$50/month, trade hay, contact..."
              />
            </label>

            <label className="form-label">
              Availability
              <select
                name="landAvailabilityStatus"
                value={landUseDetails.landAvailabilityStatus}
                onChange={handleLandUseChange}
              >
                {LAND_AVAILABILITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {landUseDetails.landAvailabilityStatus === "available_starting" && (
              <label className="form-label">
                Available starting optional
                <input
                  type="date"
                  name="availableStarting"
                  value={landUseDetails.availableStarting}
                  onChange={handleLandUseChange}
                />
              </label>
            )}
          </div>

          <label className="form-label">
            Animal/use notes optional
            <textarea
              name="animalAllowedNotes"
              value={landUseDetails.animalAllowedNotes}
              onChange={handleLandUseChange}
              rows={3}
              placeholder="Horses only, no intact males, garden use only, temporary holding..."
            />
          </label>

          {landUseDetails.landAvailabilityStatus === "available_starting" && (
            <label className="form-label">
              Availability note optional
              <textarea
                name="availabilityNote"
                value={landUseDetails.availabilityNote}
                onChange={handleLandUseChange}
                rows={3}
                placeholder="Example: Available after hay is cut in June."
              />
            </label>
          )}
        </section>
      )}

      {isLocalAlertPost && (
        <section className="community-alert-details">
          <div className="community-card-heading">
            <div>
              <p className="eyebrow">Community Alert</p>
              <h3>Alert details</h3>
            </div>
          </div>
          <p className="input-helper">
            Use alerts for time-sensitive local issues. MyFarmingPal does not verify
            community-submitted alerts, so keep details factual and report problems.
          </p>
          <label className="form-label">
            Alert expiration
            <select
              value={alertDuration}
              onChange={(event) => setAlertDuration(event.target.value)}
            >
              {COMMUNITY_ALERT_DURATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </section>
      )}

      <label className="form-label">
        Location area
        <input
          type="text"
          value={locationText}
          onChange={(event) => {
            clearFeedback();
            setFieldErrors((current) => ({ ...current, locationText: "" }));
            setLocationText(event.target.value);
          }}
          placeholder="County, state or city, state"
          aria-invalid={Boolean(fieldErrors.locationText)}
          aria-describedby={fieldErrors.locationText ? "community-location-error" : undefined}
        />
        {fieldErrors.locationText && (
          <span id="community-location-error" className="community-inline-error">
            {fieldErrors.locationText}
          </span>
        )}
        <p className="input-helper">
          Use a general area only. Please do not enter a home address.
        </p>
      </label>

      <div className="community-photo-picker">
        <label className="form-label">
          Photo optional
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handlePhotoChange}
            aria-invalid={Boolean(fieldErrors.photo)}
            aria-describedby={fieldErrors.photo ? "community-photo-error" : undefined}
          />
          {fieldErrors.photo && (
            <span id="community-photo-error" className="community-inline-error">
              {fieldErrors.photo}
            </span>
          )}
        </label>
        <p className="input-helper">JPEG, PNG, or WebP only. Max 5MB.</p>
        {photoPreviewUrl && (
          <div className="community-photo-preview">
            <div className="community-post-image-frame">
              <img
                src={photoPreviewUrl}
                alt="Selected community post preview"
                className="community-post-image"
                style={{
                  objectPosition: `${imagePositionX}% ${imagePositionY}%`,
                }}
              />
            </div>
            <div className="community-image-position-controls">
              <span>Use arrows to adjust your photo.</span>
              <div className="community-image-nudge-buttons" aria-label="Image position controls">
                <button type="button" onClick={() => nudgeImagePosition("y", -5)}>
                  ↑
                </button>
                <button type="button" onClick={() => nudgeImagePosition("x", -5)}>
                  ←
                </button>
                <button type="button" onClick={resetImagePosition}>
                  Center
                </button>
                <button type="button" onClick={() => nudgeImagePosition("x", 5)}>
                  →
                </button>
                <button type="button" onClick={() => nudgeImagePosition("y", 5)}>
                  ↓
                </button>
              </div>
            </div>
            <button
              type="button"
              className="btn-soft"
              onClick={() => {
                clearFeedback();
                setFieldErrors((current) => ({ ...current, photo: "" }));
                setPhoto(null);
              }}
            >
              Remove photo
            </button>
          </div>
        )}
      </div>

      {isAdmin && (
        <section className="community-admin-options-wrap">
          <button
            type="button"
            className="community-admin-options-toggle"
            onClick={() => setShowAdminOptions((current) => !current)}
          >
            {showAdminOptions ? "Admin options ▴" : "Admin options ▾"}
          </button>

          {showAdminOptions && (
            <div className="community-admin-options">
              <label>
                <input
                  type="checkbox"
                  checked={pinned}
                  onChange={(event) => setPinned(event.target.checked)}
                />
                Pin this post
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={isFounderPost}
                  onChange={(event) => setIsFounderPost(event.target.checked)}
                />
                Founder/Admin post
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={notify}
                  onChange={(event) => setNotify(event.target.checked)}
                />
                Send community notifications
              </label>
            </div>
          )}
        </section>
      )}

      <div className="community-create-footer">
        <p>Posts are shown locally by default within 100 miles.</p>
        <button type="submit" className="btn-primary" disabled={submitting}>
          Preview Post
        </button>
      </div>

      {(formError || formSuccess || submitting) && (
        <div
          className={`community-compose-status${
            formError ? " is-error" : formSuccess ? " is-success" : ""
          }`}
          role={formError ? "alert" : "status"}
          aria-live="polite"
        >
          {submitting ? "Posting..." : formError || formSuccess}
        </div>
      )}

      {isPreviewing && (
        <section className="community-preview-panel">
          <div className="community-card-heading">
            <div>
              <p className="eyebrow">Preview</p>
              <h3>Review before posting</h3>
            </div>
          </div>

          <article
            className={`community-post-card community-post-preview${
              previewDisplaysAsBrand ? " is-official-post" : ""
            }${pinned ? " is-pinned-post" : ""}`}
          >
            <div className="community-post-topline">
              <div className="community-post-badges">
                {isAdmin && pinned && (
                  <span className="community-type-badge type-pinned">Pinned</span>
                )}
                {isAdmin && isFounderPost && (
                  <span className="community-type-badge type-founder">
                    Official MyFarmingPal Post
                  </span>
                )}
                <span className={`community-type-badge type-${type || "general"}`}>
                  {getTypeLabel(type)}
                </span>
                {isLocalAlertPost && (
                  <span className="community-type-badge type-local-alert-status">
                    Community Alert
                  </span>
                )}
              </div>
              <span>Preview</span>
            </div>

            <h3>{title.trim()}</h3>
            <p className="community-post-text">{text.trim()}</p>

            {isLocalAlertPost && previewAlertPost && (
              <div className="community-alert-inline-note">
                <strong>Alert:</strong> {getCommunityAlertExpiryLabel(previewAlertPost)}
              </div>
            )}

            {isWantAdPost && (
              <div className="want-ad-details-box">
                <div className="exchange-badge-row">
                  <span className="exchange-badge">Looking For</span>
                  <span className="exchange-badge">
                    {getWantedCategoryLabel(wantedCategory)}
                  </span>
                  {getExchangeBadges(buildBarterPayload(barterFields)).map((badge) => (
                    <span className="exchange-badge" key={badge}>
                      {badge}
                    </span>
                  ))}
                </div>
                {budgetText.trim() && (
                  <p>
                    <strong>Budget/trade:</strong> {budgetText.trim()}
                  </p>
                )}
              </div>
            )}

            {isLandUsePost && (
              <>
                <div className="land-use-detail-strip">
                  <span>{landUseDetails.offeringOrSeeking === "offering" ? "Offering" : "Seeking"}</span>
                  <span>{getLandUseTypeLabel(landUseDetails.landUseType)}</span>
                  {landUseDetails.acreage.trim() && <span>{landUseDetails.acreage.trim()}</span>}
                  <span>Fenced: {landUseDetails.fenced}</span>
                  <span>Water: {landUseDetails.waterAccess}</span>
                  <span>{landUseDetails.priceType}</span>
                  <span>{getLandAvailabilityLabel(landUseDetails.landAvailabilityStatus)}</span>
                  {landUseDetails.landAvailabilityStatus === "available_starting" &&
                    landUseDetails.availableStarting && (
                      <span>
                        Starting{" "}
                        {new Date(`${landUseDetails.availableStarting}T00:00:00`).toLocaleDateString()}
                      </span>
                    )}
                </div>
                {landUseDetails.landAvailabilityStatus === "available_starting" &&
                  landUseDetails.availabilityNote.trim() && (
                    <p className="availability-note">
                      {landUseDetails.availabilityNote.trim()}
                    </p>
                  )}
              </>
            )}

            {photoPreviewUrl && (
              <div className="community-post-image-frame">
                <img
                  src={photoPreviewUrl}
                  alt={title.trim() || "Community post preview"}
                  className="community-post-image"
                  style={{
                    objectPosition: `${imagePositionX}% ${imagePositionY}%`,
                  }}
                />
              </div>
            )}

            {previewDisplaysAsBrand ? (
              <div className="community-official-author">
                <span className="community-official-icon">MFP</span>
                <div>
                  <strong>Official MyFarmingPal Post</strong>
                  {previewLocationLabel && <span>📍 {previewLocationLabel}</span>}
                </div>
              </div>
            ) : (
              <div className="community-post-author">
                <FarmAvatar
                  profile={{
                    id: currentUser?.uid,
                    uid: currentUser?.uid,
                    name: authorName,
                    photo: authorPhoto,
                    farmAnimal: userProfile?.farmAnimal,
                    farmAnimalSelectionExplicit:
                      userProfile?.farmAnimalSelectionExplicit === true,
                  }}
                  className="community-post-author-avatar"
                />

                <div>
                  <strong>{authorName}</strong>
                  <span>
                    {Array.isArray(userProfile?.roles) && userProfile.roles.length > 0
                      ? userProfile.roles.map(getProfileRoleLabel).join(", ")
                      : userProfile?.type || "MyFarmingPal member"}
                  </span>
                </div>
              </div>
            )}

            <div className="community-post-meta">
              {!previewDisplaysAsBrand && <span>{previewLocationLabel}</span>}
              <span>Hat Tips and comments are disabled in preview.</span>
            </div>
          </article>

          <div className="community-preview-actions">
            <button
              type="button"
              className="btn-soft"
              onClick={() => setIsPreviewing(false)}
              disabled={submitting}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={publishPost}
              disabled={submitting}
            >
              {submitting ? "Posting..." : "Post"}
            </button>
          </div>
        </section>
      )}
    </form>
  );
};

export default CreateCommunityPost;
